import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import API from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { OohCaseRow, OohEmpty } from "../components/ooh/OohCaseList";
import OohCaseDetail from "../components/ooh/OohCaseDetail";
import OohNewCaseDialog from "../components/ooh/OohNewCaseDialog";
import OohTeamDialog from "../components/ooh/OohTeamDialog";
import { ukTime } from "../components/ooh/oohUtils";
import { Moon, Sun, Plus, UsersThree, MagnifyingGlass, Siren, Lock, Headset } from "@phosphor-icons/react";

const VIEWS = [["open", "Open"], ["urgent", "Call-outs"], ["handover", "Handed over"], ["closed", "Closed"], ["all", "All"]];

function useIsDesktop() {
  const [desktop, setDesktop] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const fn = (e) => setDesktop(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  return desktop;
}

function DeskStatus({ state, canManage, onRota }) {
  if (!state) return null;
  const active = state.active;
  const oc = state.on_call;
  return (
    <div className={`rounded-md border px-4 py-2.5 flex flex-wrap items-center justify-between gap-2 text-sm ${active ? "border-indigo-400 bg-indigo-50/80 dark:bg-indigo-950/30" : "border-border bg-muted/40"}`} data-testid="ooh-desk-status">
      <span className="inline-flex items-center gap-2 font-semibold">
        {active ? <Moon size={16} weight="fill" className="text-indigo-600" /> : <Sun size={16} weight="fill" className="text-amber-500" />}
        {active ? `Out of hours desk is ACTIVE${state.reason === "weekend" ? " — weekend" : state.reason === "bank_holiday" ? " — bank holiday" : ""}` : "Day hours — the day team is on"}
      </span>
      <span className="inline-flex items-center gap-1.5 text-xs" data-testid="ooh-on-call">
        <Headset size={14} weight="fill" className={oc ? "text-emerald-600" : "text-amber-500"} />
        {oc ? <><strong>{oc.name}</strong> is on call {active ? "tonight" : "for tonight"}{oc.phone ? <a href={`tel:${oc.phone}`} className="font-mono text-indigo-700 hover:underline" data-testid="ooh-on-call-phone">{oc.phone}</a> : null}</>
          : <>Nobody on the rota {active ? "tonight" : "for tonight"} — alerts go to the whole OOH team{canManage ? <button type="button" onClick={onRota} className="underline text-indigo-700 ml-1" data-testid="ooh-set-rota-link">set the rota</button> : null}</>}
      </span>
      <span className="text-xs text-muted-foreground">
        {active ? `Day team takes over ${ukTime(state.handover_at)} · tenant reports & inbox emails are landing here` : `Desk opens ${ukTime(state.next_open_at)} · ${String(state.settings?.start_hour).padStart(2, "0")}:00–${String(state.settings?.end_hour).padStart(2, "0")}:00 weekdays, all day weekends & bank holidays`}
      </span>
    </div>
  );
}

function Tiles({ counts, view, onView }) {
  const t = [["new", "Needs triage", "text-amber-600", "open"], ["urgent", "Call-outs open", "text-red-600", "urgent"], ["self_help", "Self-help / made safe", "text-sky-600", "open"], ["awaiting_handover", "For the day team", "text-indigo-600", "open"], ["closed_today", "Closed today", "text-muted-foreground", "closed"]];
  const primary = { open: "new", urgent: "urgent", closed: "closed_today" };
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="ooh-tiles">
      {t.map(([k, l, cls, v]) => (
        <button key={k} type="button" onClick={() => onView(v)} className={`rounded-md border p-3 text-left transition-colors duration-200 hover:bg-muted/60 ${k === "urgent" && counts?.[k] ? "border-red-500 bg-red-50/70 dark:bg-red-950/30" : primary[view] === k ? "border-primary/60 bg-card" : "border-border bg-card"}`} data-testid={`ooh-tile-${k}`}>
          <p className={`text-2xl font-bold tabular-nums ${cls}`} data-testid={`ooh-count-${k}`}>{counts?.[k] ?? 0}</p>
          <p className="text-xs text-muted-foreground">{l}</p>
        </button>
      ))}
    </div>
  );
}

export default function OohDeskPage() {
  const { user } = useAuth();
  const isDesktop = useIsDesktop();
  const [params, setParams] = useSearchParams();
  const deepLink = params.get("case") || "";
  const wantNew = params.get("new") === "1";
  const [view, setView] = useState(deepLink ? "all" : "open");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState(null);
  const [selectedId, setSelectedId] = useState(deepLink);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [newOpen, setNewOpen] = useState(wantNew);
  const [teamOpen, setTeamOpen] = useState(false);
  const [teamTab, setTeamTab] = useState("team");
  const [showHelp, setShowHelp] = useState(() => localStorage.getItem("ooh_help_hidden") !== "1");
  const toggleHelp = (v) => { setShowHelp(v); localStorage.setItem("ooh_help_hidden", v ? "0" : "1"); };
  useEffect(() => { if (wantNew) { setNewOpen(true); setParams((p) => { p.delete("new"); return p; }, { replace: true }); } }, [wantNew, setParams]);

  const load = useCallback(async () => {
    try {
      const [{ data }, { data: c }] = await Promise.all([API.get("/ooh/cases", { params: { view, search, limit: 150 } }), API.get("/ooh/counts")]);
      setItems(data.items); setCounts(c); setDenied(false);
    } catch (e) { if (e.response?.status === 403) setDenied(true); else toast.error("Couldn't load the OOH desk"); }
    finally { setLoading(false); }
  }, [view, search]);
  useEffect(() => { const t = setTimeout(load, search ? 250 : 0); return () => clearTimeout(t); }, [load, search]);
  useEffect(() => { const i = setInterval(load, 20000); return () => clearInterval(i); }, [load]);

  const loadSelected = useCallback(async () => {
    if (!selectedId) { setSelected(null); return; }
    try { const { data } = await API.get(`/ooh/cases/${selectedId}`); setSelected(data); }
    catch { setSelected(null); }
  }, [selectedId]);
  useEffect(() => { loadSelected(); }, [loadSelected, items]);
  const aiReady = selected?.ai_status === "ready" || selected?.ai_status === "fallback";
  useEffect(() => {
    if (!selected || aiReady) return;
    const t = setInterval(loadSelected, 4000);
    return () => clearInterval(t);
  }, [selected, aiReady, loadSelected]);
  useEffect(() => { if (aiReady) load(); }, [aiReady]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (deepLink && deepLink !== selectedId) { setView("all"); setSelectedId(deepLink); } }, [deepLink]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (id) => { setSelectedId(id); if (deepLink) setParams((p) => { p.delete("case"); return p; }, { replace: true }); };
  const changeView = (v) => { setView(v); pick(""); };
  const changed = () => { load(); loadSelected(); };

  if (denied) {
    return (
      <div className="p-6 max-w-xl mx-auto text-center space-y-3" data-testid="ooh-denied">
        <Lock size={36} className="mx-auto text-muted-foreground" />
        <h1 className="text-xl font-[Chivo] font-bold">Out of Hours desk</h1>
        <p className="text-sm text-muted-foreground">The desk is for OOH admins and Operations Directors. Ask an Operations Director to add you as a specialist OOH admin or to give you cover for your shift.</p>
      </div>
    );
  }

  const detail = selected ? <OohCaseDetail key={selected.id} c={selected} onChanged={changed} /> : null;
  return (
    <div className="p-4 sm:p-6 min-h-[calc(100vh-4rem)] flex flex-col gap-4" data-testid="ooh-desk-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-[Chivo] font-bold tracking-tight flex items-center gap-2"><Moon size={26} weight="duotone" className="text-indigo-600" /> Out of Hours desk</h1>
          {showHelp ? (
            <p className="text-sm text-muted-foreground mt-1 max-w-3xl" data-testid="ooh-explainer">
              Requests that land between 17:00 and 08:00, at weekends or on bank holidays come here. Triage each one: urgent → raise the emergency job and find a contractor; non-urgent → talk the caller through making it safe tonight, then the day team books a cheaper visit at 08:00. Anything that reaches you another way — a call, WhatsApp, text, a forwarded email — log it with <strong>New OOH request</strong> and it gets the same AI triage.
              <button type="button" onClick={() => toggleHelp(false)} className="ml-2 underline text-xs" data-testid="ooh-explainer-hide">Hide</button>
            </p>
          ) : (
            <button type="button" onClick={() => toggleHelp(true)} className="text-xs text-muted-foreground underline mt-1" data-testid="ooh-explainer-show">How the desk works</button>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setTeamTab("team"); setTeamOpen(true); }} data-testid="ooh-team-btn"><UsersThree size={16} /> Team, rota & hours</Button>
          <Button onClick={() => setNewOpen(true)} data-testid="ooh-new-btn"><Plus size={16} weight="bold" /> New OOH request</Button>
        </div>
      </div>

      <DeskStatus state={counts?.state} canManage={counts?.state?.can_manage} onRota={() => { setTeamTab("rota"); setTeamOpen(true); }} />
      <Tiles counts={counts} view={view} onView={changeView} />
      {counts?.urgent ? (
        <div className="rounded-md border-2 border-red-500 bg-red-50 dark:bg-red-950/30 px-4 py-2.5 text-sm font-semibold text-red-800 dark:text-red-200 flex items-center justify-between gap-2" data-testid="ooh-urgent-strip">
          <span className="inline-flex items-center gap-2"><Siren size={16} weight="fill" /> {counts.urgent} call-out{counts.urgent === 1 ? "" : "s"} open — keep these until the contractor is on site and the case is closed.</span>
          <Button size="sm" variant="outline" className="border-red-400 text-red-700 hover:bg-red-100" onClick={() => changeView("urgent")} data-testid="ooh-urgent-strip-btn">Show call-outs</Button>
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-[380px_minmax(0,1fr)] gap-4 items-start">
        <div className="flex flex-col gap-3 min-w-0 lg:sticky lg:top-4">
          <div className="flex gap-1 rounded-md border border-border p-0.5 overflow-x-auto" data-testid="ooh-view-chips">
            {VIEWS.map(([k, l]) => <button key={k} type="button" onClick={() => changeView(k)} className={`px-3 py-1.5 text-xs rounded whitespace-nowrap ${view === k ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid={`ooh-view-${k}`}>{l}</button>)}
          </div>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search reference, name, phone, postcode…" className="pl-8 h-8 text-xs" data-testid="ooh-search" />
          </div>
          <div className={`rounded-md border ${items.length ? "border-border bg-card" : "border-dashed border-border/80"} flex flex-col`}>
            <div className="max-h-[55vh] min-h-[120px] overflow-y-auto" data-testid="ooh-case-list">
              {loading ? <p className="text-xs text-muted-foreground p-4">Loading…</p> : items.length === 0 ? <OohEmpty view={view} active={counts?.state?.active} /> : items.map((c) => <OohCaseRow key={c.id} c={c} active={c.id === selectedId} onClick={() => pick(c.id)} />)}
            </div>
            <div className="px-3 py-1.5 border-t border-border/60 text-[10px] text-muted-foreground tabular-nums" data-testid="ooh-list-footer">{items.length} case{items.length === 1 ? "" : "s"}{user?.name ? ` · signed in as ${user.name}` : ""}</div>
          </div>
        </div>
        {isDesktop && (
          <div className={`rounded-md border min-w-0 ${selected ? "border-border bg-card" : "border-dashed border-border/80 flex items-center justify-center min-h-[240px]"}`} data-testid="ooh-detail-pane">
            {selected ? detail : <p className="text-sm text-muted-foreground text-center px-6" data-testid="ooh-detail-placeholder">Select a case to see the AI triage, phone the requester with the script, and either raise a call-out or make it safe for the morning.</p>}
          </div>
        )}
        {!isDesktop && selected && (
          <div className="fixed inset-0 z-40 bg-background flex flex-col" data-testid="ooh-detail-mobile">
            <div className="p-2 border-b border-border"><Button variant="ghost" size="sm" onClick={() => setSelectedId("")}>← Back to list</Button></div>
            <div className="flex-1 min-h-0 overflow-y-auto">{detail}</div>
          </div>
        )}
      </div>

      <OohNewCaseDialog open={newOpen} onOpenChange={setNewOpen} onCreated={(c) => { setView("open"); setSelectedId(c.id); load(); }} />
      <OohTeamDialog open={teamOpen} onOpenChange={setTeamOpen} onChanged={load} initialTab={teamTab} />
    </div>
  );
}
